/**
 * AGP — bespoke Company page (2026 redesign handoff, Page 3; served at /about-us).
 *
 * The route stays /about-us — existing links, TENANT_ROUTES and the seeded layout
 * all key on it — while the nav label is the redesign's "Company".
 *
 * Copy + images resolve through `useSiteContent(AGP_SITE)` (`company.*` group);
 * defaults are the handoff copy verbatim. The six certificate cards are
 * text-only there (AGP's real cert1–3.png scans are still committed under
 * public/tenants/agp/ if they're ever wanted back).
 */
import { AGP_SITE } from '@mlm/shared';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { renderLines, renderRichParas } from '../../../storefront/lib/rich-paras';
import { AgpShell } from './shell';

export function AgpCompany(): JSX.Element {
  const sc = useSiteContent(AGP_SITE);
  return (
    <AgpShell active="about">
      <div className="agp-page-hero">
        <div className="agp-label">{sc.text('company.hero.label')}</div>
        <div className="agp-rule centered" />
        <h1>{sc.text('company.hero.title')}</h1>
        <p>{sc.text('company.hero.sub')}</p>
      </div>

      {/* Pillars */}
      <div className="agp-hairline-grid cols-3">
        {sc.items('company.pillars.items').map((p) => (
          <div key={p.num} className="agp-pillar warm">
            <div className="agp-pillar-num">{p.num}</div>
            <div className="agp-pillar-title">{p.title}</div>
            <p className="agp-pillar-body">{p.body}</p>
          </div>
        ))}
      </div>

      {/* Foundation */}
      <div className="agp-split">
        <div className="agp-split-content">
          <div className="agp-label">{sc.text('company.foundation.label')}</div>
          <div className="agp-rule" />
          <h2 className="agp-title sm">{renderLines(sc.text('company.foundation.title'))}</h2>
          <div className="agp-lead" style={{ display: 'grid', gap: 20 }}>
            {renderRichParas(sc.paras('company.foundation.body'))}
          </div>
        </div>
        <div
          className="agp-split-img"
          style={{ backgroundImage: `url('${sc.img('company.foundation.image')}')` }}
        />
      </div>

      {/* Certificates */}
      <section className="agp-section">
        <div className="agp-label">{sc.text('company.certs.label')}</div>
        <div className="agp-rule" />
        <h2 className="agp-title sm">{sc.text('company.certs.title')}</h2>
        <div className="agp-certs">
          {sc.items('company.certs.items').map((c) => (
            <div key={c.name} className="agp-cert">
              <div className="agp-cert-type">{c.type}</div>
              <div className="agp-cert-name">{c.name}</div>
              <div className="agp-cert-detail">{c.detail}</div>
            </div>
          ))}
        </div>
      </section>
    </AgpShell>
  );
}
